/**
 * De afleidingen achter Sharp's banen-diagram.
 *
 * Twee vragen die het canvas voortdurend stelt, en die allebei uit het model
 * volgen in plaats van uit een vinkje: **in welke baan hoort deze stap** en
 * **is deze stroom een overdracht**. De invarianten staan in §11a van
 * `archimate-sharp-workflow-extensie.md`; dit bestand is de uitvoering.
 *
 * Bewust puur en zonder store- of React-afhankelijkheid: dezelfde functies
 * worden straks door de renderer én door de validatie gebruikt, en een regel
 * die op twee plekken anders uitpakt is geen regel.
 */
import type { Concept, Relation } from "./types";

/** Stereotypes die een concept tot stap maken. */
export const STAP_STEREOTYPES = ["sharp.WorkflowStep", "sharp.DecisionStep"] as const;

/** Stereotypes die een concept tot baan-bewoner maken. Een gewone
 *  BusinessActor of Role kan óók een baan bezetten — vandaar dat de
 *  baan-bepaling niet op deze lijst leunt maar op de toewijzing. */
export const BAAN_STEREOTYPES = [
  "sharp.Pool",
  "sharp.CommitteeActor",
  "sharp.SystemActor",
  "sharp.DeviceActor",
  "sharp.HoldingArea",
  // Een baan voor werk dat de werkstroom verlaat: geen uitvoerder maar een
  // bestemming. Sharp tekent 'm als onderste baan (H8).
  "sharp.OtherProcess",
  // "An actor could even be another process" (H10 p217-218): elk proces dat
  // zelf geen stap is mag een baan bezetten. Zie `PROCES_KOPPELING` voor
  // de relatie die de stap dan aan die baan hangt.
  "sharp.BusinessProcess",
  "sharp.Subprocess",
  "sharp.ProcessArea",
] as const;

/**
 * Types die uit zichzelf een baan kunnen bezetten, ook zonder Sharp-
 * stereotype.
 *
 * Sharp's banen zijn rollen, mensen, afdelingen, systemen en machines; alleen
 * de bijzondere soorten (pool, commissie, wachtplek) hebben een stereotype
 * nodig om zich van een gewone actor te onderscheiden. Zonder deze lijst
 * heeft een kale Rol of Actor op het diagram geen baan, en is er dus ook geen
 * plek om er een stap in te slepen — terwijl de toewijzing juist zo hoort te
 * ontstaan (§11a punt 1). Beide 3.1-namen staan erin: de weergaveschakelaar
 * levert vóór de type-samenvoeging nog een `BusinessRole`.
 */
export const UITVOERDER_TYPES = [
  "archimate.BusinessActor",
  "archimate.Role",
  "archimate.BusinessRole",
  "archimate.Collaboration",
  "archimate.BusinessCollaboration",
  "archimate.ApplicationComponent",
  "archimate.SystemSoftware",
  "archimate.Equipment",
  "archimate.Location",
] as const;

const TOEWIJZING = "archimate.Assignment";

/**
 * De relatie waarmee een stap in de baan van een PROCES ligt.
 *
 * Een uitvoerder VOERT een stap uit — dat is een Assignment. Een proces voert
 * niets uit; het OMVAT de stap, en de officiële relatietabel (C260 B.5) staat
 * geen Assignment van proces naar proces toe. Onze matrix ook niet, dus een
 * stap in de baan van «Ander proces» kreeg stilletjes geen koppeling.
 *
 * Daarom een Aggregation, met een eigen stereotype. Dat stereotype is geen
 * versiering: een gewone Aggregation van een bedrijfsproces naar z'n stappen
 * komt ook op de procesmap voor, en die mag dit canvas niet als baan lezen —
 * of, erger, weggooien wanneer je de stap naar een andere baan sleept.
 */
export const PROCES_KOPPELING = "archimate.Aggregation";
export const PROCES_KOPPELING_STEREOTYPE = "sharp.ProcessLaneMembership";

/** Koppelt deze relatie een stap aan een baan — van een uitvoerder of van een proces? */
export function isBaanKoppeling(r: Relation): boolean {
  if (r.typeId === TOEWIJZING) return true;
  return (
    r.typeId === PROCES_KOPPELING && (r.stereotypes ?? []).includes(PROCES_KOPPELING_STEREOTYPE)
  );
}

/** Met welke relatie hangt een stap aan de baan van deze bewoner? */
export function koppelingVoor(bewoner: Pick<Concept, "typeId">): {
  typeId: string;
  stereotypes: string[];
} {
  return bewoner.typeId === "archimate.Process"
    ? { typeId: PROCES_KOPPELING, stereotypes: [PROCES_KOPPELING_STEREOTYPE] }
    : { typeId: TOEWIJZING, stereotypes: [] };
}
const STROOM_TYPES = ["archimate.Flow", "archimate.Triggering"];

const leeft = (r: Relation): boolean => !r.tombstoneInBranch;

export function isStap(c: Concept): boolean {
  return (c.stereotypes ?? []).some((s) => (STAP_STEREOTYPES as readonly string[]).includes(s));
}

/** Een beslisstap: de enige stap die meer dan één uitgaande tak mag hebben. */
export function isBeslisstap(c: Pick<Concept, "stereotypes">): boolean {
  return (c.stereotypes ?? []).includes("sharp.DecisionStep");
}

/**
 * Regel A (besloten 2026-09-23): wat samenkomt of splitst doet dat zichtbaar.
 *
 * Twee lijnen die in één stap uitkomen betekenen dat het werk van beide kanten
 * tegelijk binnenkomt — parallel. Een modelleur die twee pijlen tekent is zich
 * daar zelden van bewust, en een lezer weet niet of het "allebei", "één van
 * beide" of "wat er maar komt" is. Daarom hoogstens één inkomende stroom per
 * stap; samenkomen gaat via een XOR, AND of OR, en dan staat de betekenis
 * op het diagram.
 *
 * Voor splitsen geldt hetzelfde, met één uitzondering: een BESLISSTAP mag
 * meerdere uitgaande takken hebben. Die zijn per definitie exclusief en dragen
 * hun voorwaarde als label ("ja" / "nee"), dus daar is niets dubbelzinnigs aan.
 *
 * Omgekeerd is een stap met een XOR erachter altijd een beslisstap: iets moet
 * kiezen welke tak het wordt. Staat dat stereotype er niet op, dan meldt
 * `moetBeslissen` dat. `xorIds` zijn de XOR-vertakkingen op het diagram.
 *
 * `stromen` zijn de getekende stromen op het diagram — Flow, Triggering en
 * Realization (die laatste naar een procesresultaat telt als uitgang). Wat op
 * een ander diagram staat telt niet: de regel gaat over wat je hier ziet.
 */
export function stroomRegel(
  stap: Pick<Concept, "id" | "stereotypes">,
  stromen: readonly Pick<Relation, "sourceConceptId" | "targetConceptId">[],
  xorIds: ReadonlySet<string> = new Set(),
): { in: number; uit: number; teVeelIn: boolean; teVeelUit: boolean; moetBeslissen: boolean } {
  const inkomend = stromen.filter((r) => r.targetConceptId === stap.id).length;
  const uit = stromen.filter((r) => r.sourceConceptId === stap.id);
  return {
    in: inkomend,
    uit: uit.length,
    teVeelIn: inkomend > 1,
    teVeelUit: uit.length > 1 && !isBeslisstap(stap),
    moetBeslissen: !isBeslisstap(stap) && uit.some((r) => xorIds.has(r.targetConceptId)),
  };
}

/** Het stereotype van een XOR-vertakking. */
export const XOR_STEREOTYPE = "sharp.ExclusiveBranch";

/**
 * Een stap met een XOR erachter IS een beslisstap (H8 p228: een XOR
 * "corresponds to our 'decide'"). Geeft de stereotypes terug met
 * «Beslisstap» in plaats van «Werkstap»: de beslisstap is een sub-stereotype
 * dat de werkstap vervangt, niet ernaast komt. Andere stereotypes blijven.
 * Is het al een beslisstap, dan komt dezelfde lijst terug.
 */
export function alsBeslisstap(stereotypes: readonly string[]): string[] {
  if (stereotypes.includes("sharp.DecisionStep")) return [...stereotypes];
  return [...stereotypes.filter((st) => st !== "sharp.WorkflowStep"), "sharp.DecisionStep"];
}

/**
 * Kan dit concept een BAAN bezetten?
 *
 * De tegenhanger van `isStap`, en daarmee de derde soort op het canvas: wat
 * geen stap is hoeft nog geen baan te zijn. Een procesresultaat, een
 * gebeurtenis of een werkobject hoort gewoon IN een baan te liggen. Zolang
 * het canvas alleen op `isStap` splitste werd zo'n object stil tot baan
 * gemaakt: het kreeg een rij, bleef op (0,0) liggen en verdween achter de
 * titelbalk, en een uitvoerder die je daarna op die kop liet vallen haalde
 * het voorkomen helemaal van het diagram (2026-09-17).
 */
export function isBaanBewoner(c: Concept): boolean {
  if (isStap(c)) return false;
  return (
    (c.stereotypes ?? []).some((s) => (BAAN_STEREOTYPES as readonly string[]).includes(s)) ||
    (UITVOERDER_TYPES as readonly string[]).includes(c.typeId)
  );
}

/**
 * Wie voert deze stap uit — oftewel: in welke baan staat 'ie.
 *
 * Een gewone stap heeft er één. Een stap die banen overspant
 * (`is_collaborative`) heeft er meer: één primaire en de rest ondersteunend,
 * onderscheiden via `collaboration_role` op de toewijzing. De primaire bepaalt
 * waar 'ie z'n anker heeft; het geheel bepaalt of een uitgaande stroom een
 * overdracht is.
 */
export function uitvoerdersVan(
  stapId: string,
  relations: readonly Relation[],
): { primair?: string; alle: string[] } {
  const toewijzingen = relations.filter(
    (r) => leeft(r) && isBaanKoppeling(r) && r.targetConceptId === stapId,
  );
  if (toewijzingen.length === 0) return { alle: [] };
  const primair =
    toewijzingen.find((r) => r.attributes?.collaboration_role === "primary") ?? toewijzingen[0];
  return {
    ...(primair ? { primair: primair.sourceConceptId } : {}),
    alle: toewijzingen.map((r) => r.sourceConceptId),
  };
}

/**
 * Onder welke pool valt deze baan?
 *
 * Een pool is een BusinessActor met «Pool» die via een toewijzing rollen of
 * actoren onder zich heeft (§11a punt 4). Een baan zonder pool geeft
 * `undefined` — dat is de normale situatie.
 */
export function poolVan(
  baanId: string,
  concepts: readonly Concept[],
  relations: readonly Relation[],
): Concept | undefined {
  const pools = new Set(
    concepts.filter((c) => (c.stereotypes ?? []).includes("sharp.Pool")).map((c) => c.id),
  );
  if (pools.size === 0) return undefined;
  const link = relations.find(
    (r) =>
      leeft(r) &&
      r.typeId === TOEWIJZING &&
      r.targetConceptId === baanId &&
      pools.has(r.sourceConceptId),
  );
  return link ? concepts.find((c) => c.id === link.sourceConceptId) : undefined;
}

/**
 * Is deze stroom een overdracht?
 *
 * Ja wanneer de uitvoerders van bron en doel verschillen (§11a punt 2) — met
 * twee uitzonderingen:
 *
 * - **Dezelfde pool.** Twee rollen van één persoon leveren standaard geen
 *   overdracht op; `handoffs_between_lanes` op de pool draait dat om
 *   (§11a punt 4).
 * - **Overspannende stap.** Zit de uitvoerder van het doel binnen de
 *   verzameling banen die de bronstap overspant, dan blijft het werk waar het
 *   was (§11a punt 6).
 *
 * Een stroom waarvan een uitvoerder ontbreekt levert `false` — een stap in de
 * "nog niet toegewezen"-baan draagt nog niets over.
 */
export function isOverdracht(
  relation: Relation,
  concepts: readonly Concept[],
  relations: readonly Relation[],
): boolean {
  if (!leeft(relation) || !STROOM_TYPES.includes(relation.typeId)) return false;
  const bron = uitvoerdersVan(relation.sourceConceptId, relations);
  const doel = uitvoerdersVan(relation.targetConceptId, relations);
  if (!doel.primair || bron.alle.length === 0) return false;
  // Delen bron en doel een uitvoerder, dan blijft het werk bij dezelfde
  // persoon en is er niets over te dragen — ook als die uitvoerder bij een
  // van beide niet de primaire is. Eerder telde alleen de primaire van het
  // doel, en die volgt bij een samenwerkende stap uit de volgorde waarin de
  // toewijzingen toevallig staan; dan kleurde een stroom binnen dezelfde
  // baan zich als overdracht.
  if (bron.alle.some((id) => doel.alle.includes(id))) return false;

  const bronPool = bron.primair ? poolVan(bron.primair, concepts, relations) : undefined;
  const doelPool = poolVan(doel.primair, concepts, relations);
  if (bronPool && doelPool && bronPool.id === doelPool.id) {
    return bronPool.attributes?.handoffs_between_lanes === true;
  }
  return true;
}

/** Eén baan op het canvas. `id` is `null` voor de baan met wat nog geen
 *  uitvoerder heeft — die bestaat alleen op het scherm, niet in het model. */
export type Baan = {
  id: string | null;
  bewoner?: Concept;
  pool?: Concept;
  stapIds: string[];
};

/**
 * De banen van een diagram, afgeleid uit de stappen die erop staan.
 *
 * Volgorde: banen van dezelfde pool blijven bij elkaar, daarbinnen op naam,
 * en de "nog niet toegewezen"-baan staat bovenaan omdat 'ie de dropzone is
 * (§11a punt 3). Een baan-bewoner die op het diagram staat maar nog geen
 * stappen heeft krijgt tóch een baan — anders kun je er niets in slepen.
 */
export function leidBanenAf(
  concepts: readonly Concept[],
  relations: readonly Relation[],
  conceptIdsOpDiagram: readonly string[],
): Baan[] {
  const opDiagram = new Set(conceptIdsOpDiagram);
  const perId = new Map(concepts.map((c) => [c.id, c]));
  const stappen = concepts.filter((c) => opDiagram.has(c.id) && isStap(c));

  const perBaan = new Map<string, string[]>();
  const zonder: string[] = [];
  for (const stap of stappen) {
    const u = uitvoerdersVan(stap.id, relations);
    if (!u.primair) {
      zonder.push(stap.id);
      continue;
    }
    const lijst = perBaan.get(u.primair) ?? [];
    lijst.push(stap.id);
    perBaan.set(u.primair, lijst);
  }
  // Een pool die andere banen groepeert is zelf geen baan maar de beugel
  // eromheen. Hangen er rechtstreeks stappen aan, dan is 'ie het wél — een
  // afdeling zonder uitgesplitste rollen bezet gewoon een baan.
  const groepeert = new Set(
    relations
      .filter((r) => leeft(r) && r.typeId === TOEWIJZING)
      .filter((r) => {
        const bron = perId.get(r.sourceConceptId);
        const doel = perId.get(r.targetConceptId);
        return !!bron && !!doel && !isStap(doel) && (bron.stereotypes ?? []).includes("sharp.Pool");
      })
      .map((r) => r.sourceConceptId),
  );

  // Lege banen: bewoners die wél op het diagram staan maar nog geen stap
  // hebben. Zonder deze regel kun je er niets in slepen — en dat geldt net zo
  // goed voor een kale Rol of Actor als voor de gestereotypeerde soorten.
  for (const c of concepts) {
    if (!opDiagram.has(c.id) || isStap(c) || groepeert.has(c.id)) continue;
    if (isBaanBewoner(c) && !perBaan.has(c.id)) perBaan.set(c.id, []);
  }

  const banen: Baan[] = [...perBaan.entries()].map(([id, stapIds]) => {
    const bewoner = perId.get(id);
    const pool = poolVan(id, concepts, relations);
    return {
      id,
      ...(bewoner ? { bewoner } : {}),
      ...(pool ? { pool } : {}),
      stapIds,
    };
  });

  const naam = (c?: Concept): string => c?.name?.nl ?? c?.name?.en ?? "";
  banen.sort((a, b) => {
    const poolA = naam(a.pool);
    const poolB = naam(b.pool);
    if (poolA !== poolB) return poolA.localeCompare(poolB);
    return naam(a.bewoner).localeCompare(naam(b.bewoner));
  });

  return zonder.length > 0 ? [{ id: null, stapIds: zonder }, ...banen] : banen;
}

/** Een knik in een stroom op het banen-canvas, meebewegend opgeslagen. */
export type SwimlaneKnik = { langs: number; opzij: number };

/**
 * Het stuurpunt van een stroom, gegeven de twee eindpunten en een eventuele
 * knik.
 *
 * Zonder knik is dat het midden — dezelfde ronding als voorheen. Mét knik
 * verschuift 'ie langs de lijn (`langs`) en er loodrecht vanaf (`opzij`).
 * Loodrecht op de lijn zelf, niet op het scherm: een schuine stroom tussen
 * twee banen buigt daardoor de kant op die je bedoelde, ook als de banen
 * later van hoogte wisselen.
 */
export function knikStuurpunt(
  van: { x: number; y: number },
  naar: { x: number; y: number },
  knik?: SwimlaneKnik,
): { x: number; y: number } {
  const langs = knik ? Math.min(1, Math.max(0, knik.langs)) : 0.5;
  const basisX = van.x + (naar.x - van.x) * langs;
  const basisY = van.y + (naar.y - van.y) * langs;
  if (!knik || knik.opzij === 0) return { x: basisX, y: basisY };
  const dx = naar.x - van.x;
  const dy = naar.y - van.y;
  const lengte = Math.hypot(dx, dy);
  if (lengte === 0) return { x: basisX, y: basisY + knik.opzij };
  // Loodrecht: de normaal op de richting van de lijn.
  return {
    x: basisX + (-dy / lengte) * knik.opzij,
    y: basisY + (dx / lengte) * knik.opzij,
  };
}

/**
 * De knik die hoort bij een punt waar de modelleur de lijn naartoe sleepte.
 * Het omgekeerde van `knikStuurpunt`, zodat slepen en tekenen elkaars taal
 * spreken.
 */
export function knikUitPunt(
  van: { x: number; y: number },
  naar: { x: number; y: number },
  punt: { x: number; y: number },
): SwimlaneKnik {
  const dx = naar.x - van.x;
  const dy = naar.y - van.y;
  const lengte = Math.hypot(dx, dy);
  if (lengte === 0) return { langs: 0.5, opzij: punt.y - van.y };
  const vx = punt.x - van.x;
  const vy = punt.y - van.y;
  const langs = Math.min(1, Math.max(0, (vx * dx + vy * dy) / (lengte * lengte)));
  const opzij = (vx * -dy + vy * dx) / lengte;
  return { langs, opzij: Math.round(opzij) };
}

/**
 * Wat er moet gebeuren als een baan van eigenaar wisselt (§11a punt 5).
 *
 * De baan-toewijzing *is* de Assignment, dus een baan aan iemand anders
 * koppelen is het verleggen van alle toewijzingen die de stappen in die baan
 * houden. Geen dialoog, geen verweesde stappen — behalve waar de modelleur
 * bewust heeft vastgelegd dat een stap een andere uitvoerder heeft dan de
 * baan waarin 'ie getekend staat (`explicit_assignment_override`).
 *
 * Puur, zodat de renderer en een latere validatie hetzelfde antwoord geven op
 * "welke toewijzingen verhuizen mee".
 */
export function toewijzingenBijBaanwissel(
  oudeEigenaarId: string,
  concepts: readonly Concept[],
  relations: readonly Relation[],
  opDiagram: readonly string[],
): { verhuist: string[]; blijft: string[] } {
  const zichtbaar = new Set(opDiagram);
  const stappen = new Map(
    concepts.filter((c) => zichtbaar.has(c.id) && isStap(c)).map((c) => [c.id, c] as const),
  );
  const verhuist: string[] = [];
  const blijft: string[] = [];
  for (const r of relations) {
    if (!leeft(r) || !isBaanKoppeling(r)) continue;
    if (r.sourceConceptId !== oudeEigenaarId) continue;
    const stap = stappen.get(r.targetConceptId);
    if (!stap) continue;
    if (stap.attributes?.explicit_assignment_override === true) blijft.push(r.id);
    else verhuist.push(r.id);
  }
  return { verhuist, blijft };
}

/**
 * De volgorde van de banen van boven naar beneden.
 *
 * De afleiding sorteert op naam, want dat is het enige wat 'ie weet. Welke
 * baan bovenaan hoort is een keuze van de modelleur — Sharp begint bij wie
 * het werk aanvraagt en daalt af naar wie het afhandelt. Wat een plek heeft
 * gekregen staat in die volgorde vooraan; de rest volgt zoals 'ie binnenkwam.
 *
 * De baan zonder uitvoerder staat altijd onderaan. Dat is de wachtrij voor
 * werk dat nog nergens hoort — geen baan in het verhaal — en door 'm onderaan
 * te houden schuift er niets zodra 'ie verschijnt of verdwijnt: een stap naar
 * beneden slepen maakt 'm los, en de banen erboven blijven waar ze lagen.
 */
export function sorteerBanen(banen: readonly Baan[], rij: ReadonlyMap<string, number>): Baan[] {
  return [...banen].sort((a, b) => {
    if (a.id === null) return 1;
    if (b.id === null) return -1;
    const ra = rij.get(a.id);
    const rb = rij.get(b.id);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return 0;
  });
}
